import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import PhaseBanner from "@/components/PhaseBanner";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, ArrowLeft, ChevronRight } from "lucide-react";

const PAGE_LABELS = {
  "/exhibitor/products": "Product Library", "/exhibitor/qr": "My QR Code", "/exhibitor/staff": "Staff",
  "/exhibitor/enquiries": "Enquiries", "/exhibitor/catalogues": "Catalogues", "/exhibitor/leads": "Leads",
};

function pageLabel(path, nav) {
  const n = nav.find((x) => x.to === path);
  if (n) return n.label;
  if (PAGE_LABELS[path]) return PAGE_LABELS[path];
  if (path.startsWith("/exhibitor/leads/")) return "Lead";
  if (path.startsWith("/visitor/exhibitor/")) return "Stall";
  return "Page";
}

function BackBar({ nav }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const home = nav[0];
  if (!home || pathname === home.to) return null;
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate(home.to));
  return (
    <div className="flex items-center gap-3 mb-4" data-testid="back-bar">
      <button onClick={goBack} className="h-9 px-3 rounded-xl glass border border-amber-500/20 flex items-center gap-1.5 text-sm text-slate-300 hover:text-amber-300" data-testid="shell-back-button">
        <ArrowLeft size={15} /> Back
      </button>
      <nav className="flex items-center gap-1 text-xs text-slate-400 min-w-0" data-testid="breadcrumbs">
        <button onClick={() => navigate(home.to)} className="hover:text-amber-300" data-testid="breadcrumb-home">{home.label}</button>
        <ChevronRight size={12} />
        <span className="text-slate-200 truncate">{pageLabel(pathname, nav)}</span>
      </nav>
    </div>
  );
}

export default function Shell({ children, nav = [], title }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, role } = useAuth();

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen pb-24 md:pb-8">
      {/* Top header */}
      <header className="sticky top-0 z-40 glass border-b border-amber-500/15">
        <div className="max-w-md md:max-w-7xl mx-auto px-4 h-16 md:h-20 flex items-center justify-between">
          <Brand />
          <div className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <button
                key={n.to}
                data-testid={`nav-${n.key}`}
                onClick={() => navigate(n.to)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
                  location.pathname === n.to ? "btn-gold" : "text-slate-300 hover:text-amber-300"
                }`}
              >
                <n.icon size={16} /> {n.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {(role === "exhibitor" || role === "staff") && <NotificationBell />}
            <div className="text-right hidden sm:block">
              <div className="text-sm text-slate-200 font-medium leading-tight max-w-[140px] truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-amber-400/80">{role?.replace("_", " ")}</div>
            </div>
            <button data-testid="logout-button" onClick={doLogout} className="h-9 w-9 rounded-lg glass flex items-center justify-center text-slate-300 hover:text-amber-300 transition">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {title && (
        <div className="max-w-md md:max-w-7xl mx-auto px-4 pt-6">
          <h1 className="font-display text-3xl sm:text-4xl gold-text">{title}</h1>
        </div>
      )}

      <main className="max-w-md md:max-w-7xl mx-auto px-4 py-6 fade-up">
        <BackBar nav={nav} />
        <PhaseBanner />
        {children}
      </main>

      {/* Mobile bottom nav */}
      {nav.length > 0 && (
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-amber-500/15">
          <div className="max-w-md mx-auto grid" style={{ gridTemplateColumns: `repeat(${nav.length}, 1fr)` }}>
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <button
                  key={n.to}
                  data-testid={`bottomnav-${n.key}`}
                  onClick={() => navigate(n.to)}
                  className={`flex flex-col items-center gap-1 py-3 text-[10px] font-medium transition ${active ? "text-amber-400" : "text-slate-400"}`}
                >
                  <n.icon size={20} className={active ? "scale-110 transition" : ""} />
                  {n.label}
                </button>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
}
